import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Autorenew as AutorenewIcon,
    Movie as MovieIcon,
    Psychology as PsychologyIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import {
    ActiveJob,
    ManageableSession,
    JOB_LABELS,
    JobKind,
    JobStatus,
    SessionQuestionRow,
} from './types';
import { useJobStatus } from './useJobStatus';

interface SessionActionsDialogProps {
    open: boolean;
    session: ManageableSession | null;
    onClose: () => void;
    /** Refetch the list row so the health chips reflect the outcome. */
    onSessionChanged: () => void;
    onNotify: (message: string, severity: 'success' | 'error') => void;
}

type ConfirmKind = 'recording' | 'feedback' | 'question';

interface PendingConfirm {
    kind: ConfirmKind;
    /** session_question_id, for kind === 'question'. */
    targetId?: number;
}

const CONFIRM_COPY: Record<ConfirmKind, { title: string; body: string; cta: string }> = {
    recording: {
        title: 'Rebuild the full recording?',
        body:
            'This session already has a full recording. Rebuilding will merge the '
            + 'question chunks again and replace it.',
        cta: 'Rebuild recording',
    },
    feedback: {
        title: 'Regenerate feedback for this interview?',
        body:
            'This re-runs AI evaluation for every answer and rewrites the report, '
            + 'costing roughly one AI call per question plus two more for the '
            + 'summaries. It can take several minutes and will overwrite the '
            + 'existing report.',
        cta: 'Regenerate feedback',
    },
    question: {
        title: 'Re-evaluate this answer?',
        body:
            'Scoring one answer changes the interview totals, so the full report '
            + 'is rebuilt afterwards. That means one AI call for this answer plus '
            + 'roughly one per answer and two more for the summaries, and it will '
            + 'overwrite the existing report.',
        cta: 'Re-evaluate and rebuild report',
    },
};

const SessionActionsDialog: React.FC<SessionActionsDialogProps> = ({
    open,
    session,
    onClose,
    onSessionChanged,
    onNotify,
}) => {
    const [questions, setQuestions] = useState<SessionQuestionRow[]>([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [triggering, setTriggering] = useState(false);
    const [activeJob, setActiveJob] = useState<ActiveJob | null>(null);
    const [confirm, setConfirm] = useState<PendingConfirm | null>(null);

    const sessionId = session?.id ?? null;

    const fetchQuestions = useCallback(async () => {
        if (!sessionId) return;
        setLoadingQuestions(true);
        try {
            const response = await api.get(
                `/super-admin/interview-sessions/${sessionId}/questions/`,
            );
            setQuestions(response.data || []);
        } catch (error: any) {
            console.error('Error fetching session questions:', error);
            setActionError(
                error.response?.data?.error
                || error.message
                || 'Failed to load questions',
            );
        } finally {
            setLoadingQuestions(false);
        }
    }, [sessionId]);

    useEffect(() => {
        if (!open || !sessionId) {
            setQuestions([]);
            setActiveJob(null);
            setActionError(null);
            setConfirm(null);
            return;
        }
        fetchQuestions();
    }, [open, sessionId, fetchQuestions]);

    const handleJobFinished = useCallback(
        (status: JobStatus) => {
            const label = activeJob ? JOB_LABELS[activeJob.kind] : 'Job';
            setActiveJob(null);

            if (status.error) {
                setActionError(status.error);
                onNotify(`${label} failed`, 'error');
            } else {
                onNotify(`${label} finished`, 'success');
            }

            // Refetch regardless of reported success: create_session_video can
            // skip segments and still report success, and generate_feedback may
            // re-queue answers behind transcription. The stored state is the
            // only reliable signal.
            onSessionChanged();
            fetchQuestions();
        },
        [activeJob, onNotify, onSessionChanged, fetchQuestions],
    );

    const { status: jobStatus, timedOut } = useJobStatus(
        sessionId,
        activeJob,
        handleJobFinished,
    );

    const busy = triggering || activeJob !== null;

    const trigger = async (
        kind: JobKind,
        path: string,
        body?: Record<string, unknown>,
        targetId?: number,
    ) => {
        if (!sessionId) return;
        setActionError(null);
        setTriggering(true);
        try {
            const response = await api.post(
                `/super-admin/interview-sessions/${sessionId}/${path}/`,
                body ?? {},
            );
            setActiveJob({
                kind,
                taskId: response.data.task_id,
                upstreamTaskId: response.data.upstream_task_id || undefined,
                targetId,
            });
        } catch (error: any) {
            console.error(`Error triggering ${kind}:`, error);
            setActionError(
                error.response?.data?.error
                || error.message
                || `Failed to start ${JOB_LABELS[kind].toLowerCase()}`,
            );
        } finally {
            setTriggering(false);
        }
    };

    const handleRebuildRecording = () => {
        if (session?.has_session_recording) {
            setConfirm({ kind: 'recording' });
            return;
        }
        trigger('recording', 'rebuild_recording');
    };

    const handleConfirm = () => {
        const pending = confirm;
        setConfirm(null);
        if (!pending) return;

        if (pending.kind === 'recording') {
            trigger('recording', 'rebuild_recording');
        } else if (pending.kind === 'feedback') {
            trigger('feedback', 'regenerate_feedback');
        } else if (pending.kind === 'question' && pending.targetId != null) {
            trigger(
                'question',
                'reevaluate_question',
                { session_question_id: pending.targetId },
                pending.targetId,
            );
        }
    };

    if (!session) return null;

    const canRebuild = session.question_video_count > 0;
    const canRegenerate = session.status === 'completed' || session.status === 'cancelled';

    const renderJobBanner = () => {
        if (timedOut) {
            return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Still running after 10 minutes</AlertTitle>
                    We stopped watching this job. Refresh the session to see whether
                    it finished.
                </Alert>
            );
        }
        if (!activeJob) return null;
        return (
            <Alert
                severity="info"
                icon={<CircularProgress size={18} />}
                sx={{ mb: 2 }}
            >
                {JOB_LABELS[activeJob.kind]}…
                {jobStatus?.state ? ` (${jobStatus.state.toLowerCase()})` : ''}
            </Alert>
        );
    };

    return (
        <>
            <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Typography variant="h6" fontWeight="bold">
                        Interview session #{session.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {session.candidate_name || session.candidate_email || 'Unknown candidate'}
                        {' · '}
                        {session.job_description_title || session.job_role}
                        {session.company_name ? ` · ${session.company_name}` : ''}
                    </Typography>
                </DialogTitle>

                <DialogContent dividers>
                    {renderJobBanner()}

                    {actionError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
                            <AlertTitle>Action failed</AlertTitle>
                            {/* Preserve whitespace: this often carries an ffmpeg
                                stderr tail that a human needs to read. */}
                            <Box component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {actionError}
                            </Box>
                        </Alert>
                    )}

                    {/* --- Recording ------------------------------------------- */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <MovieIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold">
                                Full interview recording
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                            <Chip
                                size="small"
                                label={`${session.question_video_count} question chunk${session.question_video_count === 1 ? '' : 's'}`}
                                color={session.question_video_count > 0 ? 'default' : 'warning'}
                            />
                            <Chip
                                size="small"
                                label={session.has_session_recording ? 'Full recording available' : 'No full recording'}
                                color={session.has_session_recording ? 'success' : 'warning'}
                            />
                        </Box>
                        <Tooltip
                            title={canRebuild ? '' : 'This session has no question chunks to merge.'}
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    startIcon={<MovieIcon />}
                                    onClick={handleRebuildRecording}
                                    disabled={busy || !canRebuild}
                                >
                                    {session.has_session_recording
                                        ? 'Rebuild full recording'
                                        : 'Create full recording'}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* --- Feedback -------------------------------------------- */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PsychologyIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold">
                                Feedback and scores
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                            <Chip
                                size="small"
                                label={session.has_feedback ? 'Report generated' : 'No report'}
                                color={session.has_feedback ? 'success' : 'warning'}
                            />
                            <Chip
                                size="small"
                                label={
                                    session.unscored_question_count > 0
                                        ? `${session.unscored_question_count} unscored answer${session.unscored_question_count === 1 ? '' : 's'}`
                                        : 'All answers scored'
                                }
                                color={session.unscored_question_count > 0 ? 'error' : 'success'}
                            />
                            {session.effective_score != null && (
                                <Chip
                                    size="small"
                                    label={`Effective score ${session.effective_score.toFixed(1)}%`}
                                />
                            )}
                        </Box>
                        <Tooltip
                            title={
                                canRegenerate
                                    ? ''
                                    : `Only completed or cancelled interviews can be evaluated (status: ${session.status}).`
                            }
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    startIcon={<AutorenewIcon />}
                                    onClick={() => setConfirm({ kind: 'feedback' })}
                                    disabled={busy || !canRegenerate}
                                >
                                    Regenerate feedback (full)
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* --- Per-question ---------------------------------------- */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Questions ({questions.length})
                            </Typography>
                            <Tooltip title="Refresh questions">
                                <IconButton size="small" onClick={fetchQuestions} disabled={loadingQuestions}>
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {loadingQuestions && questions.length === 0 ? (
                            <Box display="flex" justifyContent="center" py={3}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={48}>#</TableCell>
                                            <TableCell>Question</TableCell>
                                            <TableCell width={110}>Type</TableCell>
                                            <TableCell width={90}>Score</TableCell>
                                            <TableCell width={80} align="right">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {questions.map((row) => {
                                            const isRunning = activeJob?.kind === 'question'
                                                && activeJob.targetId === row.id;
                                            return (
                                                <TableRow key={row.id} hover>
                                                    <TableCell>{row.order}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {row.question_text || <em>No linked question</em>}
                                                        </Typography>
                                                        {row.excluded_from_scoring && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                label="Excluded from scoring"
                                                                sx={{ mt: 0.5 }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {row.question_type || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.score == null ? (
                                                            <Chip
                                                                size="small"
                                                                label="None"
                                                                color={row.excluded_from_scoring ? 'default' : 'error'}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2">
                                                                {row.score.toFixed(1)}/10
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={row.reevaluate_blocked_reason || 'Re-evaluate this answer'}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    disabled={busy || !row.can_reevaluate}
                                                                    onClick={() => setConfirm({
                                                                        kind: 'question',
                                                                        targetId: row.id,
                                                                    })}
                                                                >
                                                                    {isRunning
                                                                        ? <CircularProgress size={18} />
                                                                        : <AutorenewIcon fontSize="small" />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {questions.length === 0 && !loadingQuestions && (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    <Typography variant="body2" color="text.secondary" align="center">
                                                        This session has no questions.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} disabled={busy}>
                        {busy ? 'Working…' : 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirm !== null} onClose={() => setConfirm(null)} maxWidth="sm" fullWidth>
                {confirm && (
                    <>
                        <DialogTitle>{CONFIRM_COPY[confirm.kind].title}</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2">
                                {CONFIRM_COPY[confirm.kind].body}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setConfirm(null)}>Cancel</Button>
                            <Button onClick={handleConfirm} variant="contained" color="warning">
                                {CONFIRM_COPY[confirm.kind].cta}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default SessionActionsDialog;
